import React, { useCallback, useMemo } from 'react';
import { DatasetId } from '../../../types';
import { DerivedDataSet, SQLQueryOperation } from '../../../types/dataset.types';
import { SQLQueryViewer } from '../SQLQueryViewer';
import { useProjectData } from '../../stores/hooks/useProjectData';
import { useTabOperations } from '../../hooks/useTabOperations';
import { usePaneLayout } from '../../stores/hooks/usePaneLayout';
import { createSQLEditorTab } from '../../tab-system/TabRegistry';
import logger from '../../utils/logger';
import * as styles from './SQLQueryInfo.css.ts';

interface SQLQueryInfoProps {
  datasetId: DatasetId;
  onClose?: () => void;
}

/**
 * SQLQueryInfo
 *
 * SQLクエリ情報を表示するコンポーネント
 * InformationViewerTabContentから独立して使用可能
 */
export const SQLQueryInfo: React.FC<SQLQueryInfoProps> = ({ datasetId, onClose }) => {
  const { project, getDataset } = useProjectData();
  const paneLayout = usePaneLayout();
  const tabOperations = useTabOperations({
    project,
    layout: {
      rootPane: paneLayout.layout.rootPane,
      activeContainerId: paneLayout.layout.activeContainerId ?? null
    },
    addTab: paneLayout.addTab,
    addTabWithOptions: paneLayout.addTabWithOptions,
    closeTab: paneLayout.closeTab,
    moveTabWithinPane: paneLayout.moveTabWithinPane,
    moveTabToPane: paneLayout.moveTabToPane,
    startDrag: paneLayout.startDrag,
    endDrag: paneLayout.endDrag
  });

  const dataset = getDataset(datasetId);

  // SQLクエリ操作を取得
  const sqlOperation = useMemo(() => {
    if (dataset?.type === 'derived') {
      const derivedDataset = dataset as DerivedDataSet;
      if (derivedDataset.operation?.type === 'sql_query') {
        return derivedDataset.operation as SQLQueryOperation;
      }
    }
    return null;
  }, [dataset]);

  // ターゲットデータセット名を取得
  const targetDatasetName = useMemo(() => {
    if (sqlOperation?.targetDatasetId) {
      return getDataset(sqlOperation.targetDatasetId)?.name;
    }
    return undefined;
  }, [sqlOperation, getDataset]);

  // SQLクエリを新しいエディタで開く
  const handleCopyToEditor = useCallback(() => {
    if (!sqlOperation || !dataset) return;

    // 新しいSQLEditorタブを作成（メタデータに初期値を設定）
    const newTab = createSQLEditorTab();
    newTab.metadata = {
      ...newTab.metadata,
      initialQuery: sqlOperation.query,
      initialDatasetId: sqlOperation.targetDatasetId,
      initialOutputName: `${dataset.name}_copy`
    };

    const activeContainerId = tabOperations.getActiveContainerId();

    if (activeContainerId) {
      // タブを追加
      paneLayout.addTab(activeContainerId, newTab);
      // 現在のタブを閉じる（コールバックがある場合）
      if (onClose) {
        onClose();
      }
      logger.info('[SQLQueryInfo] Created new SQL Editor tab with query from:', dataset.name);
    }
  }, [sqlOperation, dataset, tabOperations, paneLayout, onClose]);

  if (!dataset) {
    return (
      <div className={styles.error}>
        <h3>Dataset not found</h3>
        <p>The dataset with ID "{datasetId}" could not be found.</p>
      </div>
    );
  }

  if (!sqlOperation) {
    return (
      <div className={styles.error}>
        <h3>No SQL Query</h3>
        <p>This dataset was not created using a SQL query.</p>
        <p>Dataset: {dataset.name}</p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.header}>
        <h2>SQL Query: {dataset.name}</h2>
      </div>

      <div className={styles.content}>
        <SQLQueryViewer
          operation={sqlOperation}
          targetDatasetName={targetDatasetName}
          onCopyToEditor={handleCopyToEditor}
        />

        <div className={styles.info}>
          <h3>Dataset Information</h3>
          <dl className={styles.infoList}>
            <dt>Dataset Name:</dt>
            <dd>{dataset.name}</dd>

            <dt>Dataset ID:</dt>
            <dd className={styles.monospace}>{dataset.id}</dd>

            <dt>Type:</dt>
            <dd>Derived (SQL Query)</dd>

            <dt>Target Dataset:</dt>
            <dd>{targetDatasetName || sqlOperation.targetDatasetId}</dd>

            {dataset.type === 'derived' && dataset.metadata?.createdAt && (
              <>
                <dt>Created:</dt>
                <dd>{new Date(dataset.metadata.createdAt).toLocaleString()}</dd>
              </>
            )}

            {dataset.schema && (
              <>
                <dt>Rows:</dt>
                <dd>{(dataset.rowCount || 0).toLocaleString()}</dd>

                <dt>Columns:</dt>
                <dd>{dataset.schema.columns.length}</dd>
              </>
            )}
          </dl>
        </div>
      </div>
    </>
  );
};
